// Home: a warm serif greeting plus one card per area the signed-in employee can
// access. An employee with zero grants sees an honest empty state instead.
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Card, Skeleton } from "@recruiter/shared";

import { visibleAreas } from "../lib/areas";
import { meQuery, myAreasQuery } from "../lib/queries";
import { AREA_ICON, ArrowRightIcon, LockIcon } from "../components/icons";
import "./home.css";

function firstName(email: string | null): string | null {
  if (!email) return null;
  const local = email.split("@")[0] ?? "";
  const token = local.split(/[.\-_]/)[0] ?? "";
  return token ? token.charAt(0).toUpperCase() + token.slice(1) : null;
}

function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 5) return "Working late";
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

function CardsSkeleton() {
  return (
    <div className="ad-home__grid" role="status" aria-live="polite" aria-busy="true">
      {[0, 1, 2].map((i) => (
        <Card key={i} padding="lg" className="ad-area-card">
          <Skeleton variant="rect" width={40} height={40} />
          <Skeleton variant="text" width="55%" height={18} />
          <Skeleton variant="text" width="85%" />
          <span className="ad-visually-hidden">Loading your access…</span>
        </Card>
      ))}
    </div>
  );
}

export function HomePage() {
  const me = useQuery(meQuery);
  const areas = useQuery(myAreasQuery);
  const granted = areas.data ?? [];
  const accessible = visibleAreas(granted);
  const name = firstName(me.data?.email ?? null);

  return (
    <div className="ad-page">
      <header className="ad-page__head">
        <h1 className="ad-page__title ad-home__greeting">
          {name ? `${greeting()}, ${name}.` : `${greeting()}.`}
        </h1>
        <p className="ad-page__sub">This is the Mira admin console — pick an area to work in.</p>
      </header>

      {areas.isPending ? (
        <CardsSkeleton />
      ) : accessible.length === 0 ? (
        <Card padding="lg" className="ad-home__empty">
          <span className="ad-state__icon" aria-hidden="true">
            <LockIcon />
          </span>
          <h2 className="ad-card__title">No areas yet</h2>
          <p className="ad-card__body">
            You're signed in, but you haven't been granted access to any admin area. Ask an admin
            with System Admin access to grant you one — this page will fill in on its own once they
            do.
          </p>
        </Card>
      ) : (
        <div className="ad-home__grid">
          {accessible.map((area) => {
            const AreaIcon = AREA_ICON[area.key];
            return (
              <Link key={area.key} to={area.path} className="ad-home__card">
                <Card padding="lg" className="ad-area-card" data-area={area.key}>
                  <span className="ad-area-card__icon" aria-hidden="true">
                    <AreaIcon />
                  </span>
                  <h2 className="ad-card__title">{area.label}</h2>
                  <p className="ad-card__body">{area.blurb}</p>
                  <span className="ad-area-card__cta">
                    Open
                    <ArrowRightIcon size={16} />
                  </span>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
